import { useState } from "react";
import { motion } from "framer-motion";
import { Headphones, Sparkles, Volume2 } from "lucide-react";
import { useLang, loc, UI } from "@/lib/content";
import { speak } from "@/lib/tts";
import { scrollToId } from "@/lib/api";

const JURAMENTO_IMG = "https://static.prod-images.emergentagent.com/jobs/10153df0-3925-49bb-bedd-7b928c30a14b/images/c212d277386c1f9e7d93ee3da09d318867f666c29a35adafefbbf6f3ba908859.jpeg";

const WELCOME = {
  es: "Bienvenido. Este curso está hecho para ti, que ya diste el paso más difícil: decidirte a ser ciudadano estadounidense. Aquí no encontrarás teoría complicada: encontrarás las 128 preguntas oficiales con audio, la práctica de inglés exacta para tu examen y la Entrevista Integral completa, paso a paso y en español. Avanzas a tu ritmo, desde tu celular, con un método sencillo, interactivo e ilustrativo, actualizado para 2026. Cuando termines cada módulo, no solo sabrás las respuestas: llegarás a tu entrevista con calma y con confianza. Bienvenido a Cívica. Tu ciudadanía está más cerca de lo que crees.",
  en: "Welcome. This course was made for you — you already took the hardest step: deciding to become a U.S. citizen. You won't find complicated theory here: you'll find all 128 official questions with audio, the exact English practice for your test, and the complete Full Interview Prep, step by step, in plain language. You move at your own pace, from your phone, with a simple, interactive, illustrated method, updated for 2026. Module by module, you won't just learn the answers — you'll walk into your interview calm and confident. Welcome to Cívica. Your citizenship is closer than you think.",
};

export default function IntroSection() {
  const { lang } = useLang();
  const [playing, setPlaying] = useState(false);

  const listen = async () => {
    setPlaying(true);
    try {
      const a = await speak(WELCOME[lang]);
      a.onended = () => setPlaying(false);
    } catch (e) {
      setPlaying(false);
    }
  };

  return (
    <section id="introduccion" className="relative overflow-hidden bg-paper pb-24 pt-4">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            <span className="h-px w-8 bg-heritage" /> {lang === "es" ? "Introducción al curso" : "Course introduction"}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {lang === "es" ? "Bienvenido a tu " : "Welcome to your "}
            <span className="italic text-heritage">{lang === "es" ? "nuevo camino" : "new journey"}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-inkbody sm:text-lg">
            {lang === "es"
              ? "Tu ciudadanía es mucho más que un papel: es votar, es proteger a tu familia, es tener tu casa en regla para siempre. Y la mejor parte: el examen es totalmente aprendible. Con el método correcto, aprobar dejas de ser un sueño y se vuelve un plan."
              : "Your citizenship is much more than a piece of paper: it's voting, protecting your family, making your home official forever. And the best part: this test is completely learnable. With the right method, passing stops being a dream and becomes a plan."}
          </p>
          <ul className="mt-6 space-y-2.5 text-sm font-semibold text-navy">
            {(lang === "es"
              ? ["Las 128 preguntas oficiales explicadas con audio e ilustraciones", "Inglés aplicado: solo lo que te pedirán, con dictado y práctica", "La Entrevista Integral completa con respuestas modelo"]
              : ["All 128 official questions explained with audio and illustrations", "Applied English: only what they'll ask, with dictation practice", "The complete Full Interview Prep with model answers"]
            ).map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <Sparkles size={16} className="mt-0.5 shrink-0 text-brass" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#precio"
              data-testid="intro-cta"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#precio");
              }}
              className="rounded-full bg-heritage px-7 py-3.5 text-sm font-bold text-paper shadow-lg shadow-heritage/25 transition-all hover:-translate-y-0.5 hover:bg-heritagedark"
            >
              {lang === "es" ? "Comenzar ahora" : "Start now"}
            </a>
            <button
              data-testid="intro-listen"
              onClick={listen}
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 text-sm font-bold text-navy transition-all hover:bg-white"
            >
              <Headphones size={16} /> {playing ? (lang === "es" ? "Reproduciendo…" : "Playing…") : lang === "es" ? "Escuchar la bienvenida" : "Listen to the welcome"}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="frame-arch absolute -inset-3 translate-x-3 translate-y-3 border-2 border-brass/50" />
          <div className="frame-arch relative aspect-[4/5] overflow-hidden border border-line shadow-2xl">
            <img src={JURAMENTO_IMG} alt="Ceremonia de ciudadanía" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl border border-white/20 bg-navy/80 px-4 py-3 backdrop-blur">
              <div className="flex items-center gap-2 text-paper">
                <Volume2 size={14} className="text-brass" />
                <span className="text-[11px] font-bold">{lang === "es" ? "Audio incluido en cada lección" : "Audio included in every lesson"}</span>
              </div>
              <span className="rounded-full bg-heritage px-3 py-1 font-mono text-[10px] font-bold text-paper">{lang === "es" ? "Actualizado 2026" : "Updated 2026"}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
